/**
 * jherano brand motion.
 * - [data-jh-reveal]: fades/rises into view once (stagger via --jh-i set in Liquid).
 * - [data-jh-pointer]: tracks the pointer as --jh-mx / --jh-my (fine pointers only) for glow effects.
 * - <jh-mood>: mood switcher; activates the matching [data-mood-panel] items and tints the section.
 * Everything degrades to static content without JS or with reduced motion.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(pointer: fine)');

document.documentElement.classList.add('jh-js');

/** @type {IntersectionObserver | null} */
let revealObserver = null;

function observeReveals(root = document) {
  const targets = root.querySelectorAll('[data-jh-reveal]:not(.is-in)');
  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    for (const el of targets) el.classList.add('is-in');
    return;
  }
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
  );
  for (const el of targets) revealObserver.observe(el);
}

/** @param {HTMLElement} el */
function trackPointer(el) {
  if (el.dataset.jhPointerBound) return;
  el.dataset.jhPointerBound = 'true';
  let frame = 0;
  el.addEventListener('pointermove', (event) => {
    if (!finePointer.matches || reducedMotion.matches) return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--jh-mx', `${event.clientX - rect.left}px`);
      el.style.setProperty('--jh-my', `${event.clientY - rect.top}px`);
      el.classList.add('is-lit');
    });
  });
  el.addEventListener('pointerleave', () => el.classList.remove('is-lit'));
}

function bindPointers(root = document) {
  for (const el of root.querySelectorAll('[data-jh-pointer]')) {
    if (el instanceof HTMLElement) trackPointer(el);
  }
}

class JhMood extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const active = this.querySelector('[aria-pressed="true"]');
    if (active instanceof HTMLElement) this.#select(active);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-mood]');
    if (button instanceof HTMLElement && this.contains(button)) this.#select(button);
  };

  /** @param {HTMLElement} button */
  #select(button) {
    const mood = button.dataset.mood ?? '';
    for (const other of this.querySelectorAll('button[data-mood]')) {
      other.setAttribute('aria-pressed', String(other === button));
    }
    for (const panel of this.querySelectorAll('[data-mood-panel]')) {
      if (panel instanceof HTMLElement) panel.classList.toggle('is-active', panel.dataset.moodPanel === mood);
    }
    this.style.setProperty('--jh-mood-color', button.dataset.color ?? '');
    this.dataset.mood = mood;
  }
}

if (!customElements.get('jh-mood')) customElements.define('jh-mood', JhMood);

function init(root = document) {
  observeReveals(root);
  bindPointers(root);
}

init();

// Re-run for sections added or re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
